/* eslint-disable no-nested-ternary */
/*
 *
 * Dynamic Score Reports
 *
 */

import React, { useState, useEffect, memo } from "react";
import { func, object, string, bool } from "prop-types";
import { Button, Card, Container, Alert } from "react-bootstrap";
import { connect } from "react-redux";
import Loader from "react-loader-advanced";
import { ThreeBounce } from "better-react-spinkit";
import Helmet from "react-helmet";
import queryString from "query-string";
import { websiteUrl } from "utils/env";
import {
  EVENT_PLATFORM_LOGIN_BUTTON_CLICK,
  EVENT_DYNAMIC_SCORE_REPORT_VIEW,
  EVENT_PDF_DOWNLOAD,
} from "utils/trackingConstants";
import {
  getDynamicScoreReportsData,
  getDynamicScoreReportsWorkStyles,
  setShowVIMerchandisingModal,
} from "containers/App/actions";
import {
  trackDynamicScoreReport,
  trackDSRMerchLearnMoreClicked,
} from "utils/trackingFunctions";
import VideoInterviewMerchandizingModal from "components/VideoInterviewMerchandizingModal";
import DynamicReportsNavBar from "./DynamicReportsNavBar";
import CandidateInfoSection from "./CandidateInfoSection";
import CandidateSummaryPage from "./CandidateSummaryPage/CandidateSummaryPage";
import ScoreReportPage from "./ScoreReportPage/ScoreReportPage";
import {
  getCompanyStatus,
  getDSRMerchLandingPageURL,
  getDownloadAnchorElements,
  scheduleCalendlyCallWithAmOrCsm,
} from "./helpers";
import {
  DSRMerchandisingSection,
  DSRMerchandisingSectionMobile,
} from "./DSRStyledComponents";
import { DSRMerchandisingSectionText } from "./constants";

const DynamicScoreReports = (props) => {
  const {
    dynamicReportData,
    dynamicReportDataLoaded,
    dynamicReportError,
    dynamicWorkStylesData,
    dynamicWorkStylesError,
    dynamicWorkStylesLoaded,
    onGetDynamicReportData,
    onGetDynamicWorkStyles,
    requestedDynamicReportData,
    source,
    testEventId,
    userAccountId,
    featureFlags,
    onSetShowVIMerchandisingModal,
    showVIMerchandisingModal,
  } = props;
  const [pageToShow, setPageToShow] = useState("candidateSummary"); // or scoreReport
  const [currentTestId, setCurrentTestId] = useState(null);
  const [hasReportData, setHasReportData] = useState(false);
  const [testEventInfo, setTestEventInfo] = useState(null);
  const [companyAccountId, setCompanyAccountId] = useState(null);
  const [metAllScoreRanges, setMetAllScoreRanges] = useState(true);
  const [hasAtLeastOneScoreRange, setHasAtLeastOneScoreRange] = useState(false);
  const [showWorkplaceInsightsTraits, setShowWorkplaceInsightsTraits] =
    useState(false);

  const candidateInfo = testEventInfo && testEventInfo.get("candidate");
  const hideBaseline = candidateInfo && candidateInfo.get("hideBaseline") === 1;
  const rep =
    dynamicReportData &&
    dynamicReportData.get("rep") &&
    dynamicReportData.get("rep").toJS();
  const representatives =
    dynamicReportData &&
    dynamicReportData.get("representatives") &&
    dynamicReportData.get("representatives").toJS();
  const isFreeTrial = dynamicReportData && dynamicReportData.get("isFreeTrial");
  const hsVersion = dynamicReportData && dynamicReportData.get("hsVersion");
  const isInsightUser =
    dynamicReportData && dynamicReportData.get("partnerAccountId") === 47;
  const videoInterviewsEnabled =
    dynamicReportData && dynamicReportData.get("videoInterviewsEnabled");
  const developByCriteriaEnabled =
    dynamicReportData && dynamicReportData.get("developByCriteriaEnabled");
  const merchandisingProjectsEnabled =
    dynamicReportData && dynamicReportData.get("merchandisingProjectsEnabled");

  const companyStatus = getCompanyStatus(
    developByCriteriaEnabled,
    videoInterviewsEnabled
  );
  const companyStatusConfig = featureFlags.get(companyStatus);
  const dsrMerchandisingSectionText =
    DSRMerchandisingSectionText[companyStatusConfig];
  const amOrCsm =
    companyStatusConfig && scheduleCalendlyCallWithAmOrCsm(companyStatusConfig);

  const getRepresentativeDetails = () => {
    let representativeDetails = representatives && representatives[amOrCsm];

    if (!representativeDetails) {
      if (amOrCsm === "customerSuccessManager") {
        representativeDetails =
          representatives && representatives.accountManager;
      } else if (amOrCsm === "accountManager") {
        representativeDetails =
          representatives && representatives.customerSuccessManager;
      }

      if (!representativeDetails) {
        representativeDetails =
          representatives && representatives.salesRepresentative;
      }
    }

    if (!representativeDetails) {
      representativeDetails = rep;
    }

    return representativeDetails;
  };

  const representativeDetails = getRepresentativeDetails(amOrCsm);
  const isDigitalEngagement = rep && rep.teamThresholdId === 1;

  useEffect(() => {
    if (
      !dynamicReportDataLoaded ||
      !dynamicReportData ||
      dynamicReportData.size === 0
    ) {
      return;
    }

    const testEventData = dynamicReportData.get("testEvent");
    const companyId = testEventData.get("company");
    setTestEventInfo(testEventData);
    setCompanyAccountId(companyId);
    trackDynamicScoreReport(
      EVENT_DYNAMIC_SCORE_REPORT_VIEW,
      source,
      userAccountId,
      companyId,
      testEventId,
      null,
      null,
      companyStatusConfig
    );

    const tests = dynamicReportData.get("tests");

    if (tests && tests.size > 0) {
      tests.forEach((testObj, testId) => {
        if (testObj.getIn(["baseline", "hasBaseline"])) {
          setHasAtLeastOneScoreRange(true);

          if (!testObj.getIn(["baseline", "inBaseline"])) {
            setMetAllScoreRanges(false);
          }
        }

        // Only show traits in the WIR section if EPP was taken
        if (testId === "34" && dynamicReportData.get("wir")) {
          setShowWorkplaceInsightsTraits(true);
        }
      });

      // If the URL has a ?testId= query string, navigate directly to that test's score report.
      const queryTestId = queryString.parse(window.location.search).testId;

      if (queryTestId) {
        const testToDisplay = tests.get(queryTestId);

        if (testToDisplay) {
          setCurrentTestId(String(queryTestId));
          setPageToShow("scoreReport");
        } else {
          window.history.replaceState({}, "", `/scoreReports/${testEventId}`);
        }
      }
    }

    setHasReportData(true);
  }, [dynamicReportDataLoaded]);

  /* ---------- Event Handlers ----------- */

  const handleViewDetailsButton = (event) => {
    const testId = event.target.value;
    const testToDisplay = dynamicReportData.getIn(["tests", String(testId)]);

    if (!testToDisplay) {
      return;
    }
    setCurrentTestId(String(testId));
    setPageToShow("scoreReport");
    trackDynamicScoreReport(
      EVENT_DYNAMIC_SCORE_REPORT_VIEW,
      source,
      userAccountId,
      companyAccountId,
      testEventId,
      "Report",
      testId
    );

    window.history.replaceState(
      {},
      "",
      `/scoreReports/${testEventId}?testId=${testId}`
    );
  };

  const handleBack = () => {
    setCurrentTestId(null);
    setPageToShow("candidateSummary");

    window.history.replaceState({}, "", `/scoreReports/${testEventId}`);
  };

  const handlePlatformLoginButton = () => {
    trackDynamicScoreReport(
      EVENT_PLATFORM_LOGIN_BUTTON_CLICK,
      source,
      userAccountId,
      companyAccountId,
      testEventId
    );

    window.open(websiteUrl);
  };

  const handleMerchLearnMore = () => {
    trackDSRMerchLearnMoreClicked(companyStatusConfig);
    const url = getDSRMerchLandingPageURL(
      companyStatusConfig,
      representativeDetails.firstName.toLowerCase(),
      representativeDetails.lastName.toLowerCase(),
      companyAccountId
    );
    window.open(url, "_blank");
  };

  const handleDownloadPdf = (testId, reportType, testName) => {
    trackDynamicScoreReport(
      EVENT_PDF_DOWNLOAD,
      source,
      userAccountId,
      companyAccountId,
      testEventId,
      reportType,
      testId
    );

    if (testId === "workStyles") {
      onGetDynamicWorkStyles(testEventId, "pdf");
    } else {
      onGetDynamicReportData(
        testEventId,
        null,
        null,
        currentTestId,
        "pdf",
        reportType,
        testName,
        testId !== currentTestId
      );
    }
  };

  const getDSRContent = () => {
    if (!requestedDynamicReportData) {
      return (
        <Alert className="dynamic-score-report-error" variant="danger">
          Web report access is only permitted for unique candidate test events.
        </Alert>
      );
    }

    if (dynamicReportError) {
      return (
        <Alert className="dynamic-score-report-error" variant="danger">
          {dynamicReportError}
        </Alert>
      );
    }

    return (
      <Loader
        show={!dynamicReportDataLoaded}
        message={<ThreeBounce color="#1F469A" size={50} />}
        contentBlur={30}
        messageStyle={{ height: "200px" }}
        backgroundStyle={{ backgroundColor: "transparent" }}
        priority={1}
      >
        {hasReportData ? (
          <div>
            {merchandisingProjectsEnabled &&
              !isDigitalEngagement &&
              !isFreeTrial &&
              !isInsightUser && (
                <div>
                  <DSRMerchandisingSectionMobile breakpoint={575}>
                    <Container>
                      <Card className="dsr-vi-merch-card">
                        <div className="merch-card-mobile-ctn">
                          <div className="d-flex align-items-center">
                            <img
                              src={dsrMerchandisingSectionText.icon}
                              alt="video interview assessment icon"
                              className="results-vi-merch-icon"
                            />
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: "bold",
                                paddingLeft: "8px",
                              }}
                            >
                              {dsrMerchandisingSectionText.title}
                            </span>
                          </div>
                          <div>
                            <p className="mb-0">
                              {dsrMerchandisingSectionText.description}
                            </p>
                          </div>
                          <div>
                            <Button
                              onClick={handleMerchLearnMore}
                              style={{
                                whiteSpace: "nowrap",
                              }}
                            >
                              Book a Demo
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </Container>
                  </DSRMerchandisingSectionMobile>

                  <DSRMerchandisingSection breakpoint={576}>
                    <Container>
                      <Card className="dsr-vi-merch-card">
                        <div>
                          <div className="d-flex align-items-center">
                            <img
                              src={dsrMerchandisingSectionText.icon}
                              alt="video interview assessment icon"
                              className="results-vi-merch-icon"
                            />
                          </div>
                          <div>
                            <span
                              style={{ fontSize: "16px", fontWeight: "bold" }}
                            >
                              {dsrMerchandisingSectionText.title}
                            </span>
                            <p className="mb-0">
                              {dsrMerchandisingSectionText.description}
                            </p>
                          </div>
                        </div>
                        <div>
                          <Button
                            onClick={handleMerchLearnMore}
                            style={{
                              whiteSpace: "nowrap",
                            }}
                          >
                            Learn More
                          </Button>
                        </div>
                      </Card>
                    </Container>
                  </DSRMerchandisingSection>
                </div>
              )}
            <CandidateInfoSection
              candidateInfo={candidateInfo}
              metAllScoreRanges={
                hasAtLeastOneScoreRange && metAllScoreRanges && !hideBaseline
              }
            />
            {pageToShow === "candidateSummary" && (
              <CandidateSummaryPage
                candidateFirstName={candidateInfo.get("testTakerFirstName")}
                handleDownloadPdf={handleDownloadPdf}
                handleViewDetailsButton={handleViewDetailsButton}
                hideBaseline={hideBaseline}
                showWorkplaceInsightsTraits={showWorkplaceInsightsTraits}
                tests={dynamicReportData.get("tests")}
                workplaceInsightsData={dynamicReportData.get("wir")}
              />
            )}
            {pageToShow === "scoreReport" && (
              <ScoreReportPage
                allTests={dynamicReportData.get("tests")}
                candidateInfo={candidateInfo}
                companyAccountId={companyAccountId}
                handleBack={handleBack}
                handleDownloadPdf={handleDownloadPdf}
                hideBaseline={hideBaseline}
                onGetDynamicWorkStyles={onGetDynamicWorkStyles}
                scheduledBy={testEventInfo.get("scheduledBy")}
                testEventId={testEventId}
                testId={currentTestId}
                workStylesData={dynamicWorkStylesData}
                workStylesError={dynamicWorkStylesError}
                workStylesLoaded={dynamicWorkStylesLoaded}
                featureFlags={featureFlags}
                isInsightUser={isInsightUser}
                hsVersion={hsVersion}
              />
            )}
          </div>
        ) : (
          <Alert
            className="error-messages text-center mt-5"
            variant="danger"
            style={{
              width: "600px",
              marginLeft: "auto",
              marginRight: "auto",
              display: dynamicReportDataLoaded ? "" : "none",
            }}
          >
            There is no test event info for this candidate.
          </Alert>
        )}
      </Loader>
    );
  };

  return (
    <div>
      {candidateInfo && (
        <div style={{ display: "none" }}>
          {getDownloadAnchorElements(candidateInfo)}
        </div>
      )}
      <Helmet
        title="Criteria - Candidate Summary"
        meta={[
          { name: "description", content: "Description of Candidate Summary" },
        ]}
      />
      {showVIMerchandisingModal ? (
        <VideoInterviewMerchandizingModal
          showModal={!!showVIMerchandisingModal}
          handleCloseVIMerchandizingModal={() =>
            onSetShowVIMerchandisingModal(null)
          }
          utmParams={showVIMerchandisingModal}
          isFreeTrial={isFreeTrial}
          rep={rep}
        />
      ) : null}
      <Container
        fluid
        className="px-0"
        style={{
          height:
            !requestedDynamicReportData || dynamicReportError || !hasReportData
              ? "100vh"
              : "100%",
          backgroundColor: "#edeeee",
        }}
      >
        <DynamicReportsNavBar
          handleBack={handleBack}
          handlePlatformLoginButton={handlePlatformLoginButton}
        />
        {getDSRContent()}
      </Container>
    </div>
  );
};

DynamicScoreReports.propTypes = {
  dynamicReportData: object.isRequired,
  dynamicReportDataLoaded: bool.isRequired,
  dynamicReportError: string.isRequired,
  dynamicWorkStylesData: object.isRequired,
  dynamicWorkStylesError: bool.isRequired,
  dynamicWorkStylesLoaded: bool.isRequired,
  requestedDynamicReportData: bool,
  source: string,
  onGetDynamicReportData: func.isRequired,
  onGetDynamicWorkStyles: func.isRequired,
  onSetShowVIMerchandisingModal: func.isRequired,
  testEventId: string,
  userAccountId: string,
  featureFlags: object,
  showVIMerchandisingModal: object,
};

const mapStateToProps = (state) => ({
  dynamicReportData: state.get("App").get("dynamicScoreReportsData"),
  dynamicReportDataLoaded: state
    .get("App")
    .get("dynamicScoreReportsDataLoaded"),
  dynamicReportError: state.get("App").get("dynamicScoreReportsError"),
  dynamicWorkStylesData: state
    .get("App")
    .get("dynamicScoreReportsWorkStylesData"),
  dynamicWorkStylesError: state
    .get("App")
    .get("dynamicScoreReportsWorkStylesError"),
  dynamicWorkStylesLoaded: state
    .get("App")
    .get("dynamicScoreReportsWorkStylesLoaded"),
  requestedDynamicReportData: state
    .get("App")
    .get("requestedDynamicScoreReportsData"),
  source: state.get("App").get("dynamicScoreReportsSource"),
  testEventId: state.get("App").get("dynamicScoreReportsTestEventId"),
  userAccountId: state.get("App").get("dynamicScoreReportsUserAccountId"),
  featureFlags: state.get("App").get("featureFlags"),
  showVIMerchandisingModal: state.get("App").get("showVIMerchandisingModal"),
});

function mapDispatchToProps(dispatch) {
  return {
    onGetDynamicReportData: (
      testEventId,
      source,
      userAccountId,
      testId,
      format,
      reportType,
      testName,
      second
    ) =>
      dispatch(
        getDynamicScoreReportsData(
          testEventId,
          source,
          userAccountId,
          testId,
          format,
          reportType,
          testName,
          second
        )
      ),
    onGetDynamicWorkStyles: (testEventId, format) =>
      dispatch(getDynamicScoreReportsWorkStyles(testEventId, format)),
    onSetShowVIMerchandisingModal: (value) => {
      dispatch(setShowVIMerchandisingModal(value));
    },
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(memo(DynamicScoreReports));
